import { hasFormulaReferences } from "@vzbl/integrations";
import { AnnotatedDeepLink } from "pages/api/deep-links/types";
import * as React from "react";
import { useIntegrationConfig } from "src/search/lib/useIntegrationConfig";
import { SearchState } from "src/search/types";
import { eq, eqColumnFormula, eqColumnHaving } from "src/utils/fields";
import { useEffectOnce } from "usehooks-ts";
import { AddClause } from "./question-input/AddClause";
import { ClauseSwitch } from "./question-input/ClauseSwitch";
import { Controls } from "./question-input/Controls";
import {
  Clauses,
  InputClause,
  isCompareWithInputClause,
  isFilterConditionInputClause,
  isFormulaInputClause,
  isGroupByClause,
  isHavingInputClause,
  isMeasureByClause,
  isSearchInputClause,
  isSelectInputClause,
  OrderedPrimaryClauseTypes,
  OrderedSecondaryClauseTypes,
  QuestionInputOptions,
  UseQuestionInputClauses,
} from "./question-input/definitions";
import { InputContainer } from "./question-input/InputContainer";
import styles from "./question-input/question-input.module.css";
import { Alert } from "antd";

// sometimes the state is invalid, so we need to clean it up
const validateSearchState = (state: SearchState): string[] => {
  const errors = [];
  if (state.select?.length && state.select.length > 1) {
    errors.push("More than one entity selected.");
  }
  return errors;
};

export function QuestionInput(props: {
  alternateStyle?: boolean;
  applyLabel?: string;
  collapsed?: boolean;
  floatAway?: boolean; // only take up one line of space
  disabledApply?: boolean;
  disabledCancel?: boolean;
  expandOnFocus?: boolean;
  hideDisabled?: boolean; // Dashboards don't want to show unavailable options
  inlineResetOnly?: boolean;
  onApply?: () => void;
  onChange(value: SearchState): void;
  onError?: () => void;
  afterReset?: () => void;
  handleReset?: () => void;
  options: QuestionInputOptions;
  resetIcon?: React.ReactNode; // for customization
  resetTitle?: React.ReactNode; // for customization
  seed?: string; // this indiciates an upstream change that is  the "new" state
  snippetOptionFilter?: (snippet?: AnnotatedDeepLink) => boolean;
  snippetTransformer?: (state: SearchState) => SearchState;
  value: SearchState;
  addBefore?: React.ReactNode;
  controls?: React.ReactNode;
}) {
  const clauses = useQuestionInputClauses(
    props.seed,
    props.value,
    props.onChange
  );
  const options = useQuestionInputOptions(props.options, clauses.list);

  const [hasFocus, setHasFocus] = React.useState(false);
  const [isCollapsed, setIsCollapsed] = React.useState(props.collapsed);
  const [errors, setErrors] = React.useState<string[]>([]);

  React.useEffect(() => {
    setIsCollapsed(props.collapsed);
  }, [props.collapsed]);

  React.useEffect(() => {
    setErrors(validateSearchState(props.value));
  }, [props.value]);

  const handleToggleCollapse = React.useCallback(() => {
    setIsCollapsed(!isCollapsed);
  }, [isCollapsed]);

  const { handleReset, afterReset } = props;
  const handleControlsReset = React.useCallback(() => {
    if (handleReset) {
      handleReset();
    } else {
      clauses.reset();
      afterReset && afterReset();
    }
  }, [handleReset, clauses, afterReset]);

  // group them then order them by type
  const grouped = clauses.list.reduce((acc, clause) => {
    const type = clause.type;
    if (!acc[type]) acc[type] = [];
    acc[type].push(clause);
    return acc;
  }, {} as Record<string, InputClause[]>);
  const leftList = OrderedPrimaryClauseTypes.flatMap(
    (type) => grouped[type] || []
  );
  const rightList = OrderedSecondaryClauseTypes.flatMap(
    (type) => grouped[type] || []
  );
  let prev = "";
  return (
    <InputContainer
      alternateStyle={props.alternateStyle}
      collapsed={isCollapsed}
      floatAway={props.floatAway}
      clauses={clauses}
      onFocus={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      addBefore={props.addBefore}
      addAfter={
        props.controls || (
          <Controls
            searchState={props.value}
            applyLabel={props.applyLabel}
            clauses={clauses}
            collapsed={isCollapsed}
            disabledApply={props.disabledApply}
            disabledCancel={props.disabledCancel}
            onApply={props.onApply}
            onReset={handleControlsReset}
            resetIcon={props.resetIcon}
            resetTitle={props.resetTitle}
            snippetOptionFilter={props.snippetOptionFilter}
            snippetTransformer={props.snippetTransformer}
            inlineResetOnly={props.inlineResetOnly}
            isQueryEditor={true}
            onToggleCollapse={handleToggleCollapse}
          />
        )
      }
    >
      {errors.length > 0 && (
        <div style={{ width: "100%" }}>
          {errors.map((error, idx) => (
            <Alert key={idx} showIcon message={error} type="error" />
          ))}
        </div>
      )}
      <div className={styles.clauseGroupLeft}>
        {leftList.map((clause) => {
          const sameAsLast = clause.type === prev;
          prev = clause.type;
          return (
            <ClauseSwitch
              sameAsLast={sameAsLast}
              key={clause.key}
              clause={clause}
              value={clauses.value}
              removeClause={clauses.removeClause}
              updateClause={clauses.updateClause}
            />
          );
        })}
      </div>
      <div className={styles.clauseGroupRight}>
        {rightList.map((clause) => {
          const sameAsLast = clause.type === prev;
          prev = clause.type;
          return (
            <ClauseSwitch
              sameAsLast={sameAsLast}
              key={clause.key}
              clause={clause}
              value={clauses.value}
              removeClause={clauses.removeClause}
              updateClause={clauses.updateClause}
            />
          );
        })}
      </div>
      {clauses.list.length === 0 && (
        <div
          style={{
            paddingTop: 10,
            paddingLeft: 12,
            opacity: 0.6,
            pointerEvents: "none",
            fontSize: 13,
          }}
        >
          {clauses.isSeeding ? "" : "Empty query"}
        </div>
      )}
      <AddClause
        clauses={clauses.list}
        onAdd={clauses.addClause}
        onMerge={clauses.mergeStateIntoClauses}
        options={options}
        snippetTransformer={props.snippetTransformer}
        hideDisabled={props.hideDisabled}
        value={props.value}
        onSetValue={clauses.setValue}
      />
    </InputContainer>
  );
}

function useQuestionInputClauses(
  seed: string | undefined,
  value: SearchState,
  onChange: (value: SearchState) => void
): UseQuestionInputClauses {
  const [initial, setInitial] = React.useState(value);
  const [clauses, setClauses] = React.useState<InputClause[]>([]);

  const addClause = React.useCallback(
    (
      clause: Pick<InputClause, "key" | "type" | "value">,
      status: "init" | "ready"
    ) => {
      setClauses((clauses) => [
        ...clauses,
        { ...clause, status } as InputClause,
      ]);
    },
    []
  );
  const clearClauses = React.useCallback(() => {
    setClauses([]);
  }, []);

  const removeClause = React.useCallback((clause: InputClause) => {
    setClauses((clauses) => clauses.filter(({ key }) => key !== clause.key));
  }, []);

  const updateClause = React.useCallback((clause: InputClause) => {
    setClauses((clauses) =>
      clauses.map((current) => (current.key === clause.key ? clause : current))
    );
  }, []);

  const methodsRef = React.useRef({ addClause, clearClauses });
  methodsRef.current = { clearClauses, addClause };

  const config = useIntegrationConfig()?.data;

  const setValue = React.useCallback((value: SearchState) => {
    methodsRef.current.clearClauses();
    if (value.select && value.select.length > 0)
      value.select.forEach((selection, i) => {
        methodsRef.current.addClause(
          {
            key: `select-${i}`,
            type: Clauses.SELECT,
            value: selection,
          },
          "ready"
        );
      });
    if (value.groupBys && value.groupBys.length > 0)
      value.groupBys.forEach((groupBy, i) => {
        methodsRef.current.addClause(
          {
            key: `group-by-${i}`,
            type: Clauses.GROUP_BY,
            value: groupBy,
          },
          "ready"
        );
      });
    if (value.aggregations?.length)
      value.aggregations.forEach((aggregation, i) => {
        methodsRef.current.addClause(
          {
            key: `measure-by-${i}`,
            type: Clauses.MEASURE_BY,
            value: aggregation,
          },
          "ready"
        );
      });
    if (value.filters && value.filters.length > 0)
      value.filters.forEach((condition, i) => {
        methodsRef.current.addClause(
          {
            key: `filter-condition-${i}`,
            type: Clauses.FILTER_CONDITION,
            value: condition,
          },
          "ready"
        );
      });
    if (value.search)
      methodsRef.current.addClause(
        {
          key: `search-0`,
          type: Clauses.SEARCH_TERM,
          value: value.search,
        },
        "ready"
      );
    if (value.duration || value.startDate || value.endDate)
      methodsRef.current.addClause(
        {
          key: `date-range-0`,
          type: Clauses.DATE_RANGE,
          value: {
            duration: value.duration,
            startDate: value.startDate,
            endDate: value.endDate,
          },
        },
        "ready"
      );
    if (value.compareWith)
      methodsRef.current.addClause(
        {
          key: `compare-with-0`,
          type: Clauses.COMPARE_WITH,
          value: value.compareWith,
        },
        "ready"
      );
    if ((value.formulas?.length || 0) > 0)
      value.formulas?.forEach((colFormula, i) => {
        methodsRef.current.addClause(
          {
            key: `column-formula-${i}`,
            type: Clauses.FORMULA,
            value: colFormula,
          },
          "ready"
        );
      });
    if ((value.having?.length || 0) > 0)
      value.having?.forEach((colHaving, i) => {
        methodsRef.current.addClause(
          {
            key: `column-having-${i}`,
            type: Clauses.HAVING,
            value: colHaving,
          },
          "ready"
        );
      });
  }, []);

  const [orgId] = React.useState(value.orgId);
  useEffectOnce(() => {
    setValue(value);
  });

  const [deseededFor, setDeseededFor] = React.useState(seed || "empty");
  const [seededFor, setSeededFor] = React.useState("empty");
  const valuesRef = React.useRef({ value, setValue });
  valuesRef.current = { value, setValue };
  React.useEffect(() => {
    setDeseededFor(seed || "something");
  }, [seed]);

  React.useEffect(() => {
    if (deseededFor !== seededFor) {
      valuesRef.current.setValue(valuesRef.current.value);
      setInitial(valuesRef.current.value);
      setSeededFor(deseededFor);
    }
  }, [deseededFor, seededFor]);

  const changeRef = React.useRef(onChange);
  changeRef.current = onChange;

  React.useEffect(() => {
    let state: SearchState = { orgId };
    clauses.forEach((clause) => {
      switch (clause.type) {
        case Clauses.DATE_RANGE: {
          state = {
            ...state,
            duration: undefined,
            startDate: undefined,
            endDate: undefined,
            ...clause.value,
          };
          break;
        }
        case Clauses.FORMULA: {
          const formulas = state.formulas || [];
          if (clause.value)
            state = { ...state, formulas: [...formulas, clause.value] };
          break;
        }
        case Clauses.HAVING: {
          const having = state.having || [];
          if (clause.value)
            state = { ...state, having: [...having, clause.value] };
          break;
        }
        case Clauses.COMPARE_WITH: {
          state = {
            ...state,
            compareWith: clause.value,
          };
          break;
        }
        case Clauses.FILTER_CONDITION: {
          const filters = state.filters || [];
          if (clause.value)
            state = { ...state, filters: [...filters, clause.value] };
          break;
        }
        case Clauses.GROUP_BY: {
          const groupBys = state.groupBys || [];
          if (clause.value)
            state = { ...state, groupBys: [...groupBys, clause.value] };
          break;
        }
        case Clauses.MEASURE_BY: {
          const aggregations = state.aggregations || [];
          if (clause.value)
            state = { ...state, aggregations: [...aggregations, clause.value] };
          break;
        }
        case Clauses.SEARCH_TERM: {
          state = { ...state, search: clause.value };
          break;
        }
        case Clauses.SELECT: {
          const select = state.select || [];
          if (clause.value)
            state = { ...state, select: [...select, clause.value] };
          break;
        }
      }
    });
    // keep only valid formulas
    state.formulas = state.formulas
      ? state.formulas.filter((f) =>
          hasFormulaReferences(state, f, config as any)
        )
      : undefined;
    changeRef.current(state);
  }, [clauses, orgId, config]);

  const reset = React.useCallback(() => {
    setValue(initial);
  }, [setValue, initial]);

  // This is called when a snippet is applied, or
  // anytime state needs merged in, using a side-channel to the methods above.
  const mergeStateIntoClauses = React.useCallback(
    (merging: SearchState) => {
      const f = clauses.filter.bind(clauses);
      const counts = {
        selects: f((c) => c.type === Clauses.SELECT).length,
        groupBys: f((c) => c.type === Clauses.GROUP_BY).length,
        measureBys: f((c) => c.type === Clauses.MEASURE_BY).length,
        filterConditions: f((c) => c.type === Clauses.FILTER_CONDITION).length,
        searchTerms: f((c) => c.type === Clauses.SEARCH_TERM).length,
        dateRanges: f((c) => c.type === Clauses.DATE_RANGE).length,
        compareWiths: f((c) => c.type === Clauses.COMPARE_WITH).length,
        formulas: f((c) => c.type === Clauses.FORMULA).length,
        having: f((c) => c.type === Clauses.HAVING).length,
      };
      merging.select?.forEach((select, i) => {
        const selectClause = clauses
          .filter(isSelectInputClause)
          .find((clause_) => clause_.value === select);
        if (selectClause) updateClause({ ...selectClause, value: select });
        else
          addClause(
            {
              key: `select-${counts.selects + i}`,
              type: Clauses.SELECT,
              value: select,
            },
            "ready"
          );
      });
      merging.groupBys?.forEach((groupBy, i) => {
        const groupByClause = clauses
          .filter(isGroupByClause)
          .find((clause_) => eq(clause_.value, groupBy));
        if (groupByClause) updateClause({ ...groupByClause, value: groupBy });
        else
          addClause(
            {
              key: `group-by-${counts.groupBys + i}`,
              type: Clauses.GROUP_BY,
              value: groupBy,
            },
            "ready"
          );
      });

      merging.aggregations?.forEach((aggregation, i) => {
        const aggregationClause = clauses
          .filter(isMeasureByClause)
          .find((clause_) => eq(clause_.value, aggregation));
        if (aggregationClause)
          updateClause({ ...aggregationClause, value: aggregation });
        else
          addClause(
            {
              key: `filter-measure-by-${counts.measureBys + i}`,

              type: Clauses.MEASURE_BY,
              value: aggregation,
            },
            "ready"
          );
      });
      if (merging.search) {
        const searchClause = clauses.find(isSearchInputClause);
        if (searchClause)
          updateClause({ ...searchClause, value: merging.search });
        else
          addClause(
            {
              key: `search-0`,
              type: Clauses.SEARCH_TERM,
              value: merging.search,
            },
            "ready"
          );
      }
      if (merging.compareWith) {
        const compareClause = clauses.find(isCompareWithInputClause);
        if (compareClause)
          updateClause({ ...compareClause, value: merging.compareWith });
        else
          addClause(
            {
              key: `commpare-0`,
              type: Clauses.COMPARE_WITH,
              value: merging.compareWith,
            },
            "ready"
          );
      }
      merging.filters?.forEach((filter, i) => {
        const conditionClause = clauses
          .filter(isFilterConditionInputClause)
          .find((clause_) => eq(clause_.value, filter));
        if (conditionClause)
          updateClause({ ...conditionClause, value: filter });
        else
          addClause(
            {
              key: `filter-condition-${counts.filterConditions + i}`,

              type: Clauses.FILTER_CONDITION,
              value: filter,
            },
            "ready"
          );
      });
      merging.formulas?.forEach((formula, i) => {
        const formulaClause = clauses
          .filter(isFormulaInputClause)
          .find((clause_) => eqColumnFormula(clause_.value, formula));
        if (formulaClause) updateClause({ ...formulaClause, value: formula });
        else
          addClause(
            {
              key: `column-formula-${counts.formulas + i}`,
              type: Clauses.FORMULA,
              value: formula,
            },
            "ready"
          );
      });
      merging.having?.forEach((condition, i) => {
        const havingClause = clauses
          .filter(isHavingInputClause)
          .find((clause_) => eqColumnHaving(clause_.value, condition));
        if (havingClause) updateClause({ ...havingClause, value: condition });
        else
          addClause(
            {
              key: `column-having-${counts.having + i}`,
              type: Clauses.HAVING,
              value: condition,
            },
            "ready"
          );
      });
    },
    [clauses, addClause, updateClause]
  );

  return {
    addClause,
    clearClauses,
    list: clauses,
    reset,
    removeClause,
    updateClause,
    mergeStateIntoClauses,
    value,
    isSeeding: deseededFor !== seededFor,
    setValue,
  };
}

export function useQuestionInputOptions(
  options: QuestionInputOptions,
  clauses: InputClause[]
) {
  return React.useMemo(() => {
    const clone = { ...options };
    // Disable the things you are only allowed to have 1 of.
    if (clauses.some((clause) => clause.type === Clauses.SELECT))
      clone.disableSelect = true;
    if (clauses.some((clause) => clause.type === Clauses.DATE_RANGE))
      clone.disableDateRange = true;
    if (clauses.some((clause) => clause.type === Clauses.COMPARE_WITH))
      clone.disableCompareWith = true;
    if (clauses.some((clause) => clause.type === Clauses.SEARCH_TERM))
      clone.disableSearchTerm = true;
    return clone;
  }, [clauses, options]);
}
